import { importAppleHealthExportXml, APPLE_HEALTH_ADAPTER_VERSION, type AppleHealthImportResult } from "./appleHealthImportAdapter";
import {
  getImportSessionByFingerprint,
  listDailyHealthRecords,
  listHealthImportSessions,
  saveHealthImportSession,
  upsertDailyHealthRecords
} from "./dailyHealthPersistence";
import type {
  AppleHealthImportProgress,
  DailyHealthRecord,
  HealthImportDiagnostics,
  HealthImportSession,
  HealthImportSessionStatus
} from "./dailyHealthTypes";

export type HealthDataConnectorMode = "manual_import" | "apple_health_native" | "health_connect" | "garmin_api";

export type HealthConnectorCapabilities = {
  supportsImport: boolean;
  supportsSync: boolean;
  statusLabel: string;
  supportedFileExtensions: string[];
};

export type HealthConnectorImportOptions = {
  signal?: AbortSignal;
  allowReimport?: boolean;
  sourcePreferences?: Partial<Record<string, string>>;
  onProgress?: (progress: AppleHealthImportProgress) => void;
};

export type ConnectorImportResult = {
  session: HealthImportSession;
  dailyRecords: DailyHealthRecord[];
  diagnostics: HealthImportDiagnostics;
  duplicateImportDetected: boolean;
  existingSessionId: string | null;
};

export type ConnectorStatus = {
  available: boolean;
  connectionMode: HealthDataConnectorMode;
  lastSuccessfulImportAt: string | null;
  lastImportRange: { start: string | null; end: string | null };
  note: string;
};

export interface HealthDataConnector {
  connectorId: string;
  displayName: string;
  connectionMode: HealthDataConnectorMode;
  capabilities: HealthConnectorCapabilities;
  import(file: File, options?: HealthConnectorImportOptions): Promise<ConnectorImportResult>;
  sync?: () => Promise<void>;
  getStatus(): Promise<ConnectorStatus>;
  disconnect?: () => Promise<void>;
}

function createImportSession(input: {
  file: File;
  fingerprint: string;
  status: HealthImportSessionStatus;
  startedAt: string;
  completedAt: string | null;
  parse: Pick<AppleHealthImportResult, "recordsScanned" | "recordsSupported" | "recordsIgnored" | "duplicateRecordsRemoved" | "dateRangeStart" | "dateRangeEnd">;
  warnings: string[];
  error: string | null;
}): HealthImportSession {
  return {
    id: `health-import-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    sourceType: "apple_health_export",
    fileName: input.file.name,
    fileSize: input.file.size,
    startedAt: input.startedAt,
    completedAt: input.completedAt,
    status: input.status,
    recordsScanned: input.parse.recordsScanned,
    recordsSupported: input.parse.recordsSupported,
    recordsImported: 0,
    recordsIgnored: input.parse.recordsIgnored,
    duplicateRecordsRemoved: input.parse.duplicateRecordsRemoved,
    dateRangeStart: input.parse.dateRangeStart,
    dateRangeEnd: input.parse.dateRangeEnd,
    warnings: input.warnings,
    error: input.error,
    adapterVersion: APPLE_HEALTH_ADAPTER_VERSION,
    importFingerprint: input.fingerprint
  };
}

async function latestSuccessfulImport(): Promise<HealthImportSession | null> {
  const sessions = await listHealthImportSessions();
  return sessions.find(session => session.status === "completed") ?? null;
}

export class AppleHealthExportConnector implements HealthDataConnector {
  connectorId = "apple-health-export";
  displayName = "Apple Health Export";
  connectionMode: HealthDataConnectorMode = "manual_import";
  capabilities: HealthConnectorCapabilities = {
    supportsImport: true,
    supportsSync: false,
    statusLabel: "Live sync not yet available",
    supportedFileExtensions: [".xml"]
  };

  async import(file: File, options: HealthConnectorImportOptions = {}): Promise<ConnectorImportResult> {
    const startedAt = new Date().toISOString();
    const emptyParse = {
      recordsScanned: 0,
      recordsSupported: 0,
      recordsIgnored: 0,
      duplicateRecordsRemoved: 0,
      dateRangeStart: null,
      dateRangeEnd: null
    };

    const parsed = await importAppleHealthExportXml(file, {
      signal: options.signal,
      sourcePreferences: options.sourcePreferences,
      onProgress: options.onProgress
    });

    const existingSession = await getImportSessionByFingerprint(parsed.importFingerprint);
    const duplicateImportDetected = existingSession !== null;
    if (duplicateImportDetected && !options.allowReimport) {
      const duplicateSession = createImportSession({
        file,
        fingerprint: parsed.importFingerprint,
        status: "cancelled",
        startedAt,
        completedAt: new Date().toISOString(),
        parse: {
          recordsScanned: parsed.recordsScanned,
          recordsSupported: parsed.recordsSupported,
          recordsIgnored: parsed.recordsIgnored,
          duplicateRecordsRemoved: parsed.duplicateRecordsRemoved,
          dateRangeStart: parsed.dateRangeStart,
          dateRangeEnd: parsed.dateRangeEnd
        },
        warnings: ["This export appears to have already been imported. Re-import was cancelled."],
        error: null
      });
      await saveHealthImportSession(duplicateSession);
      return {
        session: duplicateSession,
        dailyRecords: await listDailyHealthRecords(),
        diagnostics: {
          ...parsed.diagnostics,
          persistenceSaveResult: "duplicate-cancelled",
          restoreResult: "existing-data-retained"
        },
        duplicateImportDetected,
        existingSessionId: existingSession?.id ?? null
      };
    }

    if (parsed.recordsSupported === 0) {
      const message = "No supported Apple Health records were found in this export.";
      const failedSession = createImportSession({
        file,
        fingerprint: parsed.importFingerprint,
        status: "failed",
        startedAt,
        completedAt: new Date().toISOString(),
        parse: {
          recordsScanned: parsed.recordsScanned,
          recordsSupported: parsed.recordsSupported,
          recordsIgnored: parsed.recordsIgnored,
          duplicateRecordsRemoved: parsed.duplicateRecordsRemoved,
          dateRangeStart: parsed.dateRangeStart,
          dateRangeEnd: parsed.dateRangeEnd
        },
        warnings: parsed.diagnostics.warnings,
        error: message
      });
      await saveHealthImportSession(failedSession);
      throw new Error(message);
    }

    try {
      const saveResult = await upsertDailyHealthRecords(parsed.dailyRecords);
      const completedAt = new Date().toISOString();
      const session = createImportSession({
        file,
        fingerprint: parsed.importFingerprint,
        status: options.signal?.aborted ? "cancelled" : "completed",
        startedAt,
        completedAt,
        parse: {
          recordsScanned: parsed.recordsScanned,
          recordsSupported: parsed.recordsSupported,
          recordsIgnored: parsed.recordsIgnored,
          duplicateRecordsRemoved: parsed.duplicateRecordsRemoved,
          dateRangeStart: parsed.dateRangeStart,
          dateRangeEnd: parsed.dateRangeEnd
        },
        warnings: parsed.diagnostics.warnings,
        error: null
      });
      session.recordsImported = parsed.dailyRecords.length;
      await saveHealthImportSession(session);

      const diagnostics: HealthImportDiagnostics = {
        ...parsed.diagnostics,
        dailyRecordsCreated: saveResult.dailyRecordsCreated,
        dailyRecordsUpdated: saveResult.dailyRecordsUpdated,
        persistenceSaveResult: `${saveResult.backend}:${saveResult.databaseStatus}`,
        restoreResult: "ready"
      };

      return {
        session,
        dailyRecords: await listDailyHealthRecords(),
        diagnostics,
        duplicateImportDetected,
        existingSessionId: existingSession?.id ?? null
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Import failed.";
      const failedSession = createImportSession({
        file,
        fingerprint: parsed.importFingerprint,
        status: options.signal?.aborted ? "cancelled" : "failed",
        startedAt,
        completedAt: new Date().toISOString(),
        parse: {
          recordsScanned: parsed.recordsScanned,
          recordsSupported: parsed.recordsSupported,
          recordsIgnored: parsed.recordsIgnored,
          duplicateRecordsRemoved: parsed.duplicateRecordsRemoved,
          dateRangeStart: parsed.dateRangeStart,
          dateRangeEnd: parsed.dateRangeEnd
        },
        warnings: parsed.diagnostics.warnings,
        error: message
      });
      await saveHealthImportSession(failedSession);
      throw error;
    }
  }

  async getStatus(): Promise<ConnectorStatus> {
    const last = await latestSuccessfulImport();
    return {
      available: true,
      connectionMode: this.connectionMode,
      lastSuccessfulImportAt: last?.completedAt ?? null,
      lastImportRange: {
        start: last?.dateRangeStart ?? null,
        end: last?.dateRangeEnd ?? null
      },
      note: "Apple Health data is imported locally in this browser. Live HealthKit synchronization will require a future iPhone companion app."
    };
  }
}
